import { defaultCurrency } from '@/config/app';
import { normalizeCurrency } from '@/locale/format';
import type { IProduct, IProductOption, IProductToSave, ISelectedProduct } from '@/types/product';
import { formatDate } from '@/filters/dateFormat';

export function extractSelectedProductOptions(
  selectedOptions: IProductToSave['selectedOptions'],
  productOptions: IProductOption[]
): { id: number; title: string; cost: any; currency: string }[] {
  if (!selectedOptions || !Object.keys(selectedOptions).length) return [];

  return Object.entries(selectedOptions)
    .map(([id, value]) => {
      const option = productOptions.find((opt) => opt.id === Number(id));
      if (!option) return;

      const fieldOption = option.field_config.cards.find((card) => card.value === value);
      if (!option) return;

      return {
        id: Number(id),
        title: fieldOption?.title || '',
        cost: fieldOption?.cost || 0,
        currency: normalizeCurrency(fieldOption?.cost_currency || defaultCurrency),
      };
    })
    .filter((a) => !!a);
}

/**
 * Total price shown in the product budget modal footer.
 *
 * Variable products start from their chosen budget (which defaults to budget_min) and add the cost
 * of the selected options on top. Single products are priced by their options sum, or their flat
 * price when they have none. Everything else has no computable price here (returns undefined).
 */
export function calculateProductModalPrice(
  selectedProduct: IProduct | undefined,
  productToSave: Pick<IProductToSave, 'budget' | 'selectedOptions'>
): number | undefined {
  if (!selectedProduct) return undefined;

  const productOptions = selectedProduct.product_options?.data || [];
  const isVariable = selectedProduct.type === 'variable';

  let optionsPrice = 0;
  if (productOptions.length) {
    const selectedOptions = extractSelectedProductOptions(productToSave.selectedOptions, productOptions);
    const sum = selectedOptions.reduce((acc, val) => acc + val.cost, 0);
    if (selectedProduct.type === 'single') return sum;
    optionsPrice = sum;
  } else if (selectedProduct.type === 'single') {
    return selectedProduct.price || 0;
  }

  if (isVariable) return +(productToSave.budget || 0) + optionsPrice;

  return undefined;
}

export function generateSelectedProductsArray(productsToSave: IProductToSave[]) {
  const selected_products: ISelectedProduct[] = productsToSave.map((product) => {
    const options_array = Object.entries(product.selectedOptions || {});

    return {
      id: product.id,
      values: {
        start: formatDate(product.startdate, 'yyyy-MM-dd'),
        end: formatDate(product.enddate, 'yyyy-MM-dd'),
        budget: product.budget,
        metric_goal: product.metric_goal,
        spending_limit: product.spending_limit,
        product_options: options_array.length
          ? options_array.map(([id, value]) => ({ id: Number(id), value }))
          : undefined,
        vbudget_id: product.vBudget?.type !== 'customer_budget' ? product.vBudget?.id : undefined,
      },
    };
  });

  return selected_products;
}
